import 'primeflex/primeflex.css';

import React, { useEffect, useState } from 'react';
import { ProcessStatus, RoleName } from 'constants/constants';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataView } from 'primereact/dataview';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import CollectionServiceV2 from 'services/CollectionServiceV2';
import { CollectionDashboardModel } from 'services/EpdClient';
import styled from 'styled-components';
import { ButtonSmall, ErrorText, InfoText } from 'styles/Styles.styled';
import { Paginator } from 'primereact/paginator';

import CollectionCard from './CollectionCard';
import CollectionListItem from './CollectionListItem';
import WelcomeCollections from './WelcomeCollections';
import AuthService from 'services/AuthService';

const ManageCollections: React.FunctionComponent<{ companyId: string }> = ({ companyId }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [collections, setCollections] = useState<CollectionDashboardModel[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 6;
  const hasAccessToCollectionActions =
    AuthService.getUser().memberships?.some((x) => x.companyId === companyId && x.roleName !== RoleName.EPD_VERIFIER) ||
    AuthService.isAdmin();

  const createNewCollection = async () => {
    try {
      openCollection('new');
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCollections = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      const result = await CollectionServiceV2.getCollections(companyId);
      setCollections(result);
      setStatus(ProcessStatus.Success);
    } catch (error) {
      console.error(error);
      setStatus(ProcessStatus.Error);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [companyId]);

  if (status === ProcessStatus.Fetching) {
    return <InfoText>{t('messages.loading')}</InfoText>;
  }

  if (!companyId) {
    return <ErrorText>{t('manageSettings.errors.technical')}</ErrorText>;
  }

  const openCollection = async (collectionId: string) => {
    try {
      setStatus(ProcessStatus.Fetching);
      const route = EpdLinks.editCollection(collectionId);
      history.push(route);
      setStatus(ProcessStatus.Success);
    } catch {
      setStatus(ProcessStatus.Error);
    }
  };

  const onPageChange = (event: any) => {
    setCurrentPage(event.first / rowsPerPage);
  };

  const paginatedCollections = collections.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const itemTemplate = (collection: CollectionDashboardModel, layout: string) => {
    if (!collection) {
      return;
    }

    if (layout === 'list') return <CollectionListItem collection={collection} fetchCollections={fetchCollections} />;
    else if (layout === 'grid') return <CollectionCard collection={collection} fetchCollections={fetchCollections} />;
  };

  return collections.length > 0 ? (
    <>
      <ConfirmDialog />
      <div className="flex justify-content-between w-full p-2">
        <PageTitle>{t('manageCollections.collections')}</PageTitle>
        {hasAccessToCollectionActions && (
          <ButtonSmall
            style={{ paddingLeft: '1.4rem', paddingRight: '1.4rem', alignSelf: 'center' }}
            onClick={() => createNewCollection()}
          >
            {t('createCollection')}
          </ButtonSmall>
        )}
      </div>

      <div className="card" style={{ width: '100%', minHeight: '612px' }}>
        <DataView value={paginatedCollections} itemTemplate={itemTemplate} layout="grid" rows={rowsPerPage} />
      </div>

      {collections.length > rowsPerPage && (
        <PaginatorDiv>
          <Paginator
            first={currentPage * rowsPerPage}
            rows={rowsPerPage}
            template={{ layout: 'PrevPageLink PageLinks NextPageLink' }}
            totalRecords={collections.length}
            onPageChange={onPageChange}
            style={{ padding: 0, marginTop: '1rem' }}
            className="paginator-right"
          />
        </PaginatorDiv>
      )}
    </>
  ) : (
    <WelcomeCollections companyId={companyId} />
  );
};

const PageTitle = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 28px;
  line-height: 34px;
  /* identical to box height */
  /* Primary/Black */
  color: #151515;
`;

const PaginatorDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 0.5rem;
`;
export default ManageCollections;
